/**
 * Authenticated fetch utility
 *
 * Factory that creates an authFetch function bound to a base URL.
 * Automatically adds credentials and CSRF headers for state-changing ops.
 */

import { getCsrfHeaders } from "./csrf";
import { safeResponseJson } from "./safe-response-json";
import type { ApiResponse } from "./types";

/**
 * Create an authenticated fetch function bound to a base URL.
 *
 * Usage:
 * ```typescript
 * const authFetch = createAuthFetch("https://api.example.com");
 * const result = await authFetch<GameData>("/games", { method: "POST", body: ... });
 * ```
 */
export function createAuthFetch(
  baseUrl: string,
): <T>(path: string, options?: RequestInit) => Promise<ApiResponse<T>> {
  return async <T>(
    path: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> => {
    const method = options.method?.toUpperCase() ?? "GET";
    const isStateChanging = ["POST", "PUT", "DELETE", "PATCH"].includes(method);

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(isStateChanging ? getCsrfHeaders() : {}),
        ...options.headers,
      },
      credentials: "include",
    });

    return safeResponseJson<T>(response);
  };
}
